<template>
  <vs-popup
    :title="formTitle"
    :active.sync="isOpen"
    @close="handleClose"
    :class="['sales-closures-form-modal', { 'is-loading': isLoading }]"
    fullscreen
  >
    <div class="p-4 mx-auto max-w-xl">
      <div v-if="isLoading" class="sales-closures-loading-state">
        <div class="sales-closures-spinner"></div>
        <p class="text-sm text-gray-600 font-semibold">Cargando datos del cierre...</p>
      </div>

      <template v-else>
      <!-- Campaign Summary (Card minimalista) -->
      <div class="bg-white border border-gray-200 shadow-sm p-4 rounded-lg mb-4">
        <div class="flex items-center justify-between mb-3">
          <h4 class="font-bold text-[11px] tracking-wider uppercase text-gray-500 flex items-center">
            <i class="bx bx-info-circle text-latinad mr-1 text-base"></i> Resumen de Campaña
          </h4>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 text-xs mb-3">
          <div class="bg-gradient-to-br from-gray-50 to-white p-3 rounded-md border border-gray-200">
            <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide mb-1">Título</p>
            <p class="font-bold text-latinad text-sm truncate" :title="campaignBaseData.campaignName">{{ campaignBaseData.campaignName }}</p>
          </div>
          <div class="bg-gradient-to-br from-gray-50 to-white p-3 rounded-md border border-gray-200">
            <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide mb-1">ID</p>
            <p class="font-bold text-latinad text-sm">{{ campaignBaseData.campaignId }}</p>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 text-xs">
          <div class="bg-gradient-to-br from-gray-50 to-white p-3 rounded-md border border-gray-200">
            <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide mb-1">Presupuesto</p>
            <p class="font-bold text-latinad text-base leading-tight">{{ stringCurrencyFormatter(campaignBaseData.budgetTotal) }} <span class="text-sm">{{ summaryCurrency }}</span></p>
          </div>
          <div class="bg-gradient-to-br from-gray-50 to-white p-3 rounded-md border border-gray-200">
            <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide mb-1">Real (Agencia)</p>
            <p class="font-bold text-latinad text-base leading-tight">{{ stringCurrencyFormatter(campaignBaseData.consumptionReal) }} <span class="text-sm">{{ summaryCurrency }}</span></p>
          </div>
          <div class="bg-gradient-to-br from-gray-50 to-white p-3 rounded-md border border-gray-200">
            <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide mb-1">Ppto. Media</p>
            <p class="font-bold text-latinad text-base leading-tight">{{ stringCurrencyFormatter(campaignBaseData.mediaOwnerBudget) }} <span class="text-sm">{{ summaryCurrency }}</span></p>
          </div>
          <div class="bg-gradient-to-br from-gray-50 to-white p-3 rounded-md border border-gray-200">
            <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide mb-1">Real Medios</p>
            <p class="font-bold text-latinad text-base leading-tight">{{ stringCurrencyFormatter(campaignBaseData.mediaOwnerConsumption) }} <span class="text-sm">{{ summaryCurrency }}</span></p>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs mt-3">
          <div class="bg-gray-50 p-3 rounded-md border border-gray-200">
            <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide mb-1">Fechas Campaña</p>
            <p class="font-bold text-latinad text-sm">{{ campaignDateRangeText }}</p>
          </div>
          <div class="bg-gray-50 p-3 rounded-md border border-gray-200">
            <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide mb-1">Tipo de Campaña</p>
            <p class="font-bold text-latinad text-sm">{{ campaignTypeLabel }}</p>
          </div>
          <div class="bg-gray-50 p-3 rounded-md border border-gray-200">
            <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide mb-1">Exchange</p>
            <p class="font-bold text-latinad text-sm">{{ campaignBaseData.exchangeName || 'No informado' }}</p>
          </div>
          <div class="bg-gray-50 p-3 rounded-md border border-gray-200">
            <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide mb-1">DSP</p>
            <p class="font-bold text-latinad text-sm">{{ campaignBaseData.dspName || 'No informado' }}</p>
          </div>
          <div v-if="campaignBaseData.comments" class="bg-gray-50 p-3 rounded-md border border-gray-200 md:col-span-4">
            <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide mb-1">Comentario</p>
            <p class="font-bold text-latinad text-sm">{{ campaignBaseData.comments }}</p>
          </div>
          <div class="bg-gray-50 p-3 rounded-md border border-gray-200 md:col-span-4">
            <details class="cursor-pointer">
              <summary class="text-gray-500 text-[10px] uppercase font-bold tracking-wide mb-1 select-none">
                Medios involucrados ({{ campaignBaseData.mediaCount }})
              </summary>
              <div class="mt-2 text-latinad font-bold text-sm">
                {{ campaignBaseData.mediaList.join(', ') }}
              </div>
            </details>
          </div>
        </div>
      </div>

      <!-- Form Sections -->
      <form @submit.prevent="onSaveClick" class="space-y-4">

        <!-- 1. Empresa -->
        <div class="bg-white border border-gray-200 shadow-sm rounded-lg p-5">
          <div class="flex items-center mb-4 pb-2 border-b border-gray-100">
            <span class="w-6 h-5 rounded-full bg-latinad text-white flex items-center justify-center text-[10px] font-bold mr-2">{{ responsibleAgency ? '1A' : '1' }}</span>
            <h3 class="font-bold text-sm text-gray-700 tracking-wide uppercase">Empresa</h3>
          </div>
          <div class="grid grid-cols-2 gap-5">
            <!-- Nombre Fantasía -->
            <div class="flex flex-col">
              <label class="text-sm font-medium text-gray-700 mb-1">Nombre fantasía</label>
              <vs-input
                :value="isFantasyNameFixed ? companyFantasyNameWithId : formData.company_fantasy_name"
                placeholder="Ej: LatinAd"
                :readonly="isFantasyNameFixed"
                :class="[{ 'is-readonly': isFantasyNameFixed }, getFieldBorderClass('company_fantasy_name')]"
                class="w-full form-input-minimalist"
                @input="!isFantasyNameFixed && (formData.company_fantasy_name = $event); markFieldAsEdited('company_fantasy_name')"
              />
            </div>

            <!-- Razón Social -->
            <div class="flex flex-col">
              <label class="text-sm font-medium text-gray-700 mb-1">Razón social</label>
              <div :class="getFieldBorderClass('billing_information_id')">
                <v-select
                  v-model="formData.billing_information_id"
                  :options="availableCompanies"
                  :reduce="company => company.billing_information_id || company.id"
                  label="name"
                  :clearable="true"
                  :searchable="true"
                  :filterable="true"
                  :filter-by="companyFilterBy"
                  @input="onBillingInformationChange(formData.billing_information_id); markFieldAsEdited('billing_information_id')"
                  placeholder="Buscar y seleccionar razón social"
                  class="reason-social-select"
                >
                  <span slot="no-options">Sin resultados.</span>
                </v-select>
              </div>
            </div>
          </div>

          <!-- 1B. Agencia responsable (sólo campañas por exchange) -->
          <template v-if="responsibleAgency">
            <div class="flex items-center mb-4 mt-6 pb-2 border-b border-gray-100">
              <span class="w-6 h-5 rounded-full bg-latinad text-white flex items-center justify-center text-[10px] font-bold mr-2">1B</span>
              <h3 class="font-bold text-sm text-gray-700 tracking-wide uppercase">Agencia responsable</h3>
            </div>
            <div class="grid grid-cols-2 gap-5">
              <div class="flex flex-col">
                <label class="text-sm font-medium text-gray-700 mb-1">Nombre fantasía</label>
                <vs-input
                  :value="responsibleAgencyLabel"
                  readonly
                  class="w-full form-input-minimalist is-readonly"
                />
              </div>
            </div>
          </template>
        </div>

        <!-- 2. Facturación y Pago -->
        <div class="bg-white border border-gray-200 shadow-sm rounded-lg p-5">
          <div class="flex items-center mb-4 pb-2 border-b border-gray-100">
            <span class="w-5 h-5 rounded-full bg-latinad text-white flex items-center justify-center text-[10px] font-bold mr-2">2</span>
            <h3 class="font-bold text-sm text-gray-700 tracking-wide uppercase">Facturación y Pago</h3>
          </div>
          <div class="grid grid-cols-2 gap-5">
            <!-- Billing Mode -->
            <div class="flex flex-col" :class="getFieldBorderClass('billing_mode')">
              <label class="text-sm font-medium text-gray-700 mb-1">Modo de facturación</label>
              <v-select
                v-model="formData.billing_mode"
                :options="billingModeOptions"
                :reduce="option => option.value"
                label="label"
                :clearable="false"
                :searchable="true"
                @input="() => { onBillingModeChange(); markFieldAsEdited('billing_mode'); }"
                placeholder="Buscar y seleccionar"
                class="w-full form-v-select"
              >
                <span slot="no-options">Sin resultados.</span>
              </v-select>
            </div>

            <!-- Payment Mode -->
            <div class="flex flex-col" :class="getFieldBorderClass('payment_mode')">
              <label class="text-sm font-medium text-gray-700 mb-1">Modo de pago</label>
              <v-select
                v-model="formData.payment_mode"
                :options="paymentModeOptions"
                :reduce="option => option.value"
                label="label"
                :clearable="false"
                :searchable="true"
                @input="() => { onPaymentModeChange(); markFieldAsEdited('payment_mode'); }"
                placeholder="Buscar y seleccionar"
                class="w-full form-v-select"
              >
                <span slot="no-options">Sin resultados.</span>
              </v-select>
            </div>

            <!-- Payment Term -->
            <div class="flex flex-col" :class="getFieldBorderClass('payment_term')">
              <label class="text-sm font-medium text-gray-700 mb-1">Plazo de pago (días)</label>
              <vs-input
                v-model="formData.payment_term"
                type="number"
                min="0"
                @input="onIntegerNonNegativeInput('payment_term'); markFieldAsEdited('payment_term')"
                class="w-full form-input-minimalist"
              />
            </div>

            <!-- Charge Term -->
            <div class="flex flex-col" :class="getFieldBorderClass('charge_term')">
              <label class="text-sm font-medium text-gray-700 mb-1">Plazo de cobro (días)</label>
              <vs-input
                v-model="formData.charge_term"
                type="number"
                min="0"
                @input="onIntegerNonNegativeInput('charge_term'); markFieldAsEdited('charge_term')"
                class="w-full form-input-minimalist"
              />
            </div>
          </div>
        </div>

        <!-- 3. Período y Monto -->
        <div class="bg-white border border-gray-200 shadow-sm rounded-lg p-5">
          <div class="flex items-center mb-4 pb-2 border-b border-gray-100">
            <span class="w-5 h-5 rounded-full bg-latinad text-white flex items-center justify-center text-[10px] font-bold mr-2">3</span>
            <h3 class="font-bold text-sm text-gray-700 tracking-wide uppercase">Período y Monto</h3>
          </div>
          <div class="grid grid-cols-2 gap-5">
            <!-- Date From -->
            <div class="flex flex-col" :class="getFieldBorderClass('date_from')">
              <label class="text-sm font-medium text-gray-700 mb-1">Fecha desde</label>
              <vs-input
                v-model="formData.date_from"
                type="date"
                class="w-full form-input-minimalist"
                @input="handleDateFromChange"
              />
              <p v-if="dateRangeAlerts.date_from" style="background-color: #fb9e2e; color: white; padding: 0.375rem 0.75rem; border-radius: 0.5rem; font-size: 0.75rem; margin-top: 0.5rem; display: inline-block; width: fit-content;">
                {{ dateRangeAlerts.date_from }}
              </p>
            </div>

            <!-- Date To -->
            <div class="flex flex-col" :class="getFieldBorderClass('date_to')">
              <label class="text-sm font-medium text-gray-700 mb-1">Fecha hasta</label>
              <vs-input
                v-model="formData.date_to"
                type="date"
                :min="formData.date_from || null"
                class="w-full form-input-minimalist"
                @input="handleDateToChange"
              />
              <p v-if="dateRangeAlerts.date_to" style="background-color: #fb9e2e; color: white; padding: 0.375rem 0.75rem; border-radius: 0.5rem; font-size: 0.75rem; margin-top: 0.5rem; display: inline-block; width: fit-content;">
                {{ dateRangeAlerts.date_to }}
              </p>
            </div>

            <!-- Amount -->
            <div class="flex flex-col" :class="getFieldBorderClass('amount')">
              <label class="text-sm font-medium text-gray-700 mb-1">Monto</label>
              <div class="flex items-center gap-2">
                <vs-input
                  v-model="formData.amount"
                  type="number"
                  min="0"
                  step="0.01"
                  class="w-full form-input-minimalist"
                  @input="onAmountChange(); markFieldAsEdited('amount')"
                />
                <feather-icon
                  v-if="isConsumptionExceeded"
                  icon="AlertTriangleIcon"
                  svgClasses="w-5 h-5 text-warning cursor-help"
                  :title="consumptionExceededMessage"
                />
              </div>
              <p v-if="isAmountLoading" class="text-[11px] text-gray-500 mt-1">Calculando monto...</p>
            </div>

            <!-- Currency -->
            <div class="flex flex-col" :class="getFieldBorderClass('currency')">
              <label class="text-sm font-medium text-gray-700 mb-1">Moneda</label>
              <v-select
                v-model="formData.currency"
                :options="currencyOptions"
                :clearable="false"
                :searchable="true"
                placeholder="Buscar y seleccionar"
                class="w-full form-v-select"
                @input="markFieldAsEdited('currency')"
              >
                <span slot="no-options">Sin resultados.</span>
              </v-select>
            </div>
          </div>
        </div>

        <!-- 4. Comisiones e Impuestos -->
        <div class="bg-white border border-gray-200 shadow-sm rounded-lg p-5">
          <div class="flex items-center mb-4 pb-2 border-b border-gray-100">
            <span class="w-8 h-5 rounded-full bg-latinad text-white flex items-center justify-center text-[10px] font-bold mr-2">4.1</span>
            <h3 class="font-bold text-sm text-gray-700 tracking-wide uppercase">Comisiones e Impuestos</h3>
          </div>

          <div class="mb-5 w-1/2 pr-2 flex flex-col" :class="getFieldBorderClass('agency_commission_mode')">
            <label class="text-sm font-medium text-gray-700 mb-1">Modo de comisión</label>
            <v-select
              v-model="formData.agency_commission_mode"
              :options="agencyCommissionModeOptions"
              :reduce="option => option.value"
              label="label"
              :clearable="false"
              :searchable="true"
              placeholder="Buscar y seleccionar"
              class="w-full form-v-select"
              @input="markFieldAsEdited('agency_commission_mode')"
            >
              <span slot="no-options">Sin resultados.</span>
            </v-select>
          </div>

          <!-- Commission Rows -->
          <div class="space-y-4">
            <div class="bg-gray-50 p-3.5 rounded-lg border border-gray-200 relative hover:border-amber-200 transition-colors">
              <div class="absolute top-0 left-0 w-full h-1 bg-amber-400"></div>
              <p class="text-xs font-bold text-gray-800 mb-3 uppercase tracking-wide mt-1">Agencia</p>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="flex flex-col" :class="getFieldBorderClass('agency_commission_percentage')">
                  <label class="text-xs font-semibold text-gray-700 mb-1">Porcentaje (%)</label>
                  <vs-input
                    v-model="formData.agency_commission_percentage"
                    type="number"
                    min="0"
                    step="0.01"
                    @input="onCommissionPercentageChange('agency_commission_percentage'); markFieldAsEdited('agency_commission_percentage')"
                    class="w-full form-input-minimalist"
                  />
                </div>
                <div class="flex flex-col">
                  <label class="text-xs font-semibold text-gray-700 mb-1">Monto</label>
                  <vs-input
                    v-model="formData.agency_commission_amount"
                    type="number"
                    min="0"
                    step="0.01"
                    @input="onCommissionAmountChange('agency_commission_amount')"
                    class="w-full form-input-minimalist"
                  />
                </div>
                <div class="flex flex-col" :class="getFieldBorderClass('agency_commission_currency')">
                  <label class="text-xs font-semibold text-gray-700 mb-1">Moneda</label>
                  <v-select
                    v-model="formData.agency_commission_currency"
                    :options="currencyOptions"
                    :clearable="false"
                    :searchable="true"
                    class="w-full mini-select form-v-select"
                    @input="markFieldAsEdited('agency_commission_currency')"
                  >
                    <span slot="no-options">Sin resultados.</span>
                  </v-select>
                </div>
              </div>
            </div>

            <div class="bg-gray-50 p-3.5 rounded-lg border border-gray-200 relative hover:border-blue-200 transition-colors">
              <div class="absolute top-0 left-0 w-full h-1 bg-blue-500"></div>
              <p class="text-xs font-bold text-gray-800 mb-3 uppercase tracking-wide mt-1">Exchange</p>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="flex flex-col" :class="getFieldBorderClass('exchange_commission_percentage')">
                  <label class="text-xs font-semibold text-gray-700 mb-1">Porcentaje (%)</label>
                  <vs-input
                    v-model="formData.exchange_commission_percentage"
                    type="number"
                    min="0"
                    step="0.01"
                    @input="onCommissionPercentageChange('exchange_commission_percentage'); markFieldAsEdited('exchange_commission_percentage')"
                    class="w-full form-input-minimalist"
                  />
                </div>
                <div class="flex flex-col">
                  <label class="text-xs font-semibold text-gray-700 mb-1">Monto</label>
                  <vs-input
                    v-model="formData.exchange_commission_amount"
                    type="number"
                    min="0"
                    step="0.01"
                    @input="onCommissionAmountChange('exchange_commission_amount')"
                    class="w-full form-input-minimalist"
                  />
                </div>
                <div class="flex flex-col" :class="getFieldBorderClass('exchange_commission_currency')">
                  <label class="text-xs font-semibold text-gray-700 mb-1">Moneda</label>
                  <v-select
                    v-model="formData.exchange_commission_currency"
                    :options="currencyOptions"
                    :clearable="false"
                    :searchable="true"
                    class="w-full mini-select form-v-select"
                    @input="markFieldAsEdited('exchange_commission_currency')"
                  >
                    <span slot="no-options">Sin resultados.</span>
                  </v-select>
                </div>
              </div>
            </div>

            <div class="bg-gray-50 p-3.5 rounded-lg border border-gray-200 relative hover:border-emerald-200 transition-colors">
              <div class="absolute top-0 left-0 w-full h-1 bg-emerald-500"></div>
              <p class="text-xs font-bold text-gray-800 mb-3 uppercase tracking-wide mt-1 flex items-center">
                Impuestos Nacionales
                <feather-icon
                  icon="InfoIcon"
                  svgClasses="w-4 h-4 text-gray-400 ml-1 cursor-help"
                  title="Sugerido según el país de facturación de la razón social."
                />
              </p>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="flex flex-col" :class="getFieldBorderClass('national_tax_percentage')">
                  <label class="text-xs font-semibold text-gray-700 mb-1">Porcentaje (%)</label>
                  <vs-input
                    v-model="formData.national_tax_percentage"
                    type="number"
                    min="0"
                    step="0.01"
                    @input="onCommissionPercentageChange('national_tax_percentage'); markFieldAsEdited('national_tax_percentage')"
                    class="w-full form-input-minimalist"
                  />
                </div>
                <div class="flex flex-col" :class="getFieldBorderClass('national_tax_amount')">
                  <label class="text-xs font-semibold text-gray-700 mb-1">Monto</label>
                  <vs-input
                    v-model="formData.national_tax_amount"
                    type="number"
                    min="0"
                    step="0.01"
                    @input="onCommissionAmountChange('national_tax_amount'); markFieldAsEdited('national_tax_amount')"
                    class="w-full form-input-minimalist"
                  />
                </div>
                <div class="flex flex-col" :class="getFieldBorderClass('national_tax_currency')">
                  <label class="text-xs font-semibold text-gray-700 mb-1">Moneda</label>
                  <v-select
                    v-model="formData.national_tax_currency"
                    :options="currencyOptions"
                    :clearable="false"
                    :searchable="true"
                    class="w-full mini-select form-v-select"
                    @input="markFieldAsEdited('national_tax_currency')"
                  >
                    <span slot="no-options">Sin resultados.</span>
                  </v-select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 4.2 Ajuste WHT (opcional, colapsable) -->
        <div class="bg-white border border-gray-200 shadow-sm rounded-lg p-5">
          <div
            class="flex items-center justify-between pb-2 border-b border-gray-100 cursor-pointer select-none"
            role="button"
            tabindex="0"
            @click="toggleWhtSection"
            @keydown.enter.prevent="toggleWhtSection"
            @keydown.space.prevent="toggleWhtSection"
          >
            <div class="flex items-center">
              <span class="w-8 h-5 rounded-full bg-latinad text-white flex items-center justify-center text-[10px] font-bold mr-2">4.2</span>
              <h3 class="font-bold text-sm text-gray-700 tracking-wide uppercase">Ajuste WHT</h3>
              <span class="ml-2 text-[11px] text-gray-400 font-medium">(opcional)</span>
            </div>
            <feather-icon
              :icon="isWhtSectionOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              svgClasses="w-5 h-5 text-gray-400"
            />
          </div>

          <!-- Inputs always rendered: v-show keeps their values while the section is collapsed -->
          <div v-show="isWhtSectionOpen" class="mt-4 space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="flex flex-col">
                <label class="text-sm font-semibold text-gray-700 mb-1">Base de cálculo</label>
                <vs-input
                  v-model="formData.wht_calculation_base"
                  type="number"
                  min="0"
                  step="0.01"
                  class="w-full form-input-minimalist"
                  @input="onWhtBaseChange"
                />
              </div>
              <div class="flex flex-col">
                <label class="text-sm font-semibold text-gray-700 mb-1">Moneda</label>
                <v-select
                  v-model="formData.international_tax_currency"
                  :options="currencyOptions"
                  :clearable="false"
                  :searchable="true"
                  class="w-full mini-select form-v-select"
                >
                  <span slot="no-options">Sin resultados.</span>
                </v-select>
              </div>
            </div>

            <div class="bg-gray-50 p-3.5 rounded-lg border border-gray-200 relative">
              <div class="absolute top-0 left-0 w-full h-1 bg-red-500"></div>
              <p class="text-xs font-bold text-gray-800 mb-3 uppercase tracking-wide mt-1">Tax Int.</p>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="flex flex-col" :class="getFieldBorderClass('international_tax_percentage')">
                  <label class="text-xs font-semibold text-gray-700 mb-1">Porcentaje (%)</label>
                  <vs-input
                    v-model="formData.international_tax_percentage"
                    type="number"
                    min="0"
                    step="0.01"
                    @input="onWhtPercentageChange"
                    class="w-full form-input-minimalist"
                  />
                </div>
                <div class="flex flex-col">
                  <label class="text-xs font-semibold text-gray-700 mb-1">Monto</label>
                  <vs-input
                    v-model="formData.international_tax_amount"
                    type="number"
                    min="0"
                    step="0.01"
                    @input="onWhtAmountChange"
                    class="w-full form-input-minimalist"
                  />
                </div>
                <!-- Locked on purpose: the backend has a single currency for base and amount -->
                <div class="flex flex-col">
                  <label class="text-xs font-semibold text-gray-700 mb-1">Moneda</label>
                  <v-select
                    :value="formData.international_tax_currency"
                    :options="currencyOptions"
                    :clearable="false"
                    :searchable="false"
                    disabled
                    class="w-full mini-select form-v-select is-readonly"
                  >
                    <span slot="no-options">Sin resultados.</span>
                  </v-select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 5. Resumen Final -->
        <div class="bg-white border border-gray-200 shadow-sm rounded-lg p-5">
          <div class="flex items-center mb-4 pb-2 border-b border-gray-100">
            <span class="w-5 h-5 rounded-full bg-latinad text-white flex items-center justify-center text-[10px] font-bold mr-2">5</span>
            <h3 class="font-bold text-sm text-gray-700 tracking-wide uppercase">Resumen</h3>
          </div>

          <div class="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
            <div>
              <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide">Pago de Agencia</p>
              <p class="font-bold text-latinad text-base">{{ stringCurrencyFormatter(closureSummary.agency_payment) }} {{ closureSummary.currency }}</p>
            </div>
            <div>
              <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide">Pago a Medios</p>
              <p class="font-bold text-latinad text-base">{{ stringCurrencyFormatter(closureSummary.media_payment) }} {{ closureSummary.currency }}</p>
            </div>
            <div>
              <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide">Comisión de Agencia</p>
              <p class="font-bold text-latinad text-base">{{ stringCurrencyFormatter(closureSummary.agency_commission_payment) }} {{ closureSummary.currency }}</p>
            </div>
            <div>
              <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide">Comisión de Exchange</p>
              <p class="font-bold text-latinad text-base">{{ stringCurrencyFormatter(closureSummary.exchange_commission_payment) }} {{ closureSummary.currency }}</p>
            </div>

            <div>
              <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide">Impuestos Internacionales</p>
              <p class="font-bold text-latinad text-base">{{ stringCurrencyFormatter(closureSummary.international_tax_payment) }} {{ closureSummary.currency }}</p>
            </div>
            <div>
              <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide">Impuestos Nacionales</p>
              <p class="font-bold text-latinad text-base">{{ stringCurrencyFormatter(closureSummary.national_tax_payment) }} {{ closureSummary.currency }}</p>
            </div>

            <div class="border-t border-gray-100 pt-3">
              <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide">Monto Neto</p>
              <p class="font-bold text-latinad text-lg">{{ stringCurrencyFormatter(closureSummary.net_amount) }} {{ closureSummary.currency }}</p>
            </div>
            <div class="border-t border-gray-100 pt-3">
              <p class="text-gray-500 text-[10px] uppercase font-bold tracking-wide">Monto a Facturar</p>
              <p class="font-bold text-latinad text-lg">{{ stringCurrencyFormatter(closureSummary.billing_amount) }} {{ closureSummary.currency }}</p>
            </div>
          </div>
        </div>

        <!-- 6. Comentarios -->
        <div class="bg-white border border-gray-200 shadow-sm rounded-lg p-5">
          <div class="flex items-center mb-4 pb-2 border-b border-gray-100">
            <span class="w-5 h-5 rounded-full bg-latinad text-white flex items-center justify-center text-[10px] font-bold mr-2">6</span>
            <h3 class="font-bold text-sm text-gray-700 tracking-wide uppercase">Comentarios</h3>
          </div>
          <div class="grid grid-cols-1 gap-5">
            <div class="flex flex-col">
              <label class="text-sm font-medium text-gray-700 mb-1">Observación de cierre</label>
              <vs-textarea
                v-model="formData.closure_observation"
                :rows="3"
                maxlength="1000"
                placeholder="Ingrese un comentario u observaciones para explicar los cambios que se realizaron"
                class="w-full"
              />
            </div>
            <div class="flex flex-col">
              <label class="text-sm font-medium text-gray-700 mb-1">Comentario de facturación</label>
              <vs-textarea
                v-model="formData.billing_comment"
                :rows="3"
                maxlength="1000"
                placeholder="Si corresponde, ingrese instrucciones para el facturador"
                class="w-full"
              />
            </div>
          </div>
        </div>

        <!-- Acciones Finales -->
        <div class="pt-2 pb-4 space-y-4">
          <!-- Alertas -->
          <div v-if="validationErrors.length" class="validation-errors-box rounded p-4 shadow-sm">
            <p class="text-danger font-bold mb-2 text-sm uppercase flex items-center">
              <feather-icon icon="AlertCircleIcon" svgClasses="w-4 h-4 mr-2" />
              Revise los siguientes campos:
            </p>
            <ul class="list-disc list-inside text-danger text-base font-semibold">
              <li v-for="(error, idx) in validationErrors" :key="idx" class="mb-1">{{ error }}</li>
            </ul>
          </div>

          <!-- Buttons Row -->
          <div class="flex gap-3 mt-4">
            <vs-button
              color="dark"
              type="border"
              class="flex-1 font-bold tracking-wide py-2 shadow-sm transition-colors"
              @click="handleClose"
            >
              Cancelar
            </vs-button>

            <vs-button
              color="primary"
              type="filled"
              class="flex-1 font-bold tracking-wide py-2 shadow border-0"
              @click.prevent="onSaveClick"
            >
              Guardar
            </vs-button>
          </div>
        </div>

      </form>
      </template>
    </div>

    <div
      v-if="isSaveChoiceModalOpen"
      class="fixed inset-0 z-[100001] bg-black/40 flex items-center justify-center px-4"
      @click.self="handleSaveChoiceClose"
    >
      <div class="w-full max-w-md bg-white rounded-lg shadow-xl border border-gray-200 p-4">
        <div class="flex items-center justify-between mb-3">
          <h4 class="font-bold text-gray-800">Guardar cierre</h4>
          <button
            type="button"
            class="w-8 h-8 rounded-full border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-latinad/30"
            @click="handleSaveChoiceClose"
            aria-label="Cerrar"
          >
            <i class="feather icon-x text-base"></i>
          </button>
        </div>

        <p class="text-sm text-gray-700 mb-4">{{ saveChoiceDescription }}</p>

        <div class="flex gap-2">
          <vs-button
            v-if="canSaveAsDraft"
            color="warning"
            type="filled"
            class="flex-1"
            @click="onSaveChoice(CLOSURE_STATUS.DRAFT)"
          >
            Guardar como Borrador
          </vs-button>

          <vs-button
            color="primary"
            type="filled"
            :class="canSaveAsDraft ? 'flex-1' : 'w-full'"
            @click="onSaveChoice(CLOSURE_STATUS.VALID)"
          >
            {{ isEditingValidClosure ? 'Confirmar' : 'Guardar como Final' }}
          </vs-button>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<style src="./SalesClosuresForm.css"></style>

<script>
import {
  BILLING_MODES_LABELS,
  PAYMENT_MODES_LABELS,
  AGENCY_COMMISSION_MODES_LABELS,
  CLOSURE_STATUS
} from '@/constants/salesClosures'
import {
  calculateCommissionAmount,
  calculateSuggestedAmount,
  calculateNetAmount,
  calculateBillingAmount,
  generateSuggestedDateRange,
  validateNoDateOverlap,
  formatClosureForSubmit,
  OVERLAP_ERROR_MESSAGE
} from '@/utils/salesClosuresCalculator'
import { dateFormatter, stringCurrencyFormatter } from '@/utils/strings'
import appConfig from '@/../appConfig.js'
import vSelect from 'vue-select'

export default {
  name: 'SalesClosuresForm',
  components: {
    vSelect
  },
  props: {
    active: {
      type: Boolean,
      default: false
    },
    campaignId: {
      type: [String, Number],
      required: true
    },
    closure: {
      type: Object,
      default: null
    },
    existingClosures: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isOpen: false,
      isSaveChoiceModalOpen: false,
      // Collapsed by default; opened automatically when editing a closure that carries WHT
      isWhtSectionOpen: false,
      isLoading: false,
      isAmountLoading: false,
      amountRequestToken: 0,
      // Last amount-calculation response: backend is the source of truth for net/billing amounts
      liveCalculation: null,
      isFantasyNameFixed: false,
      validationErrors: [],
      // Fields that failed validation, so their input can be outlined in red
      erroredFields: [],
      dateRangeAlerts: {
        date_from: '',
        date_to: ''
      },
      availableCompanies: [],
      availableCurrencies: ['USD', 'ARS', 'BRL', 'CLP', 'COP', 'MEX', 'PEN'],
      campaignBaseData: {
        campaignId: null,
        campaignName: '',
        budgetTotal: 0,
        consumptionReal: 0,
        mediaOwnerBudget: 0,
        mediaOwnerConsumption: 0,
        currency: 'USD',
        campaignType: '',
        startDate: '',
        endDate: '',
        exchangeName: '',
        dspName: '',
        comments: '',
        mediaCount: 0,
        mediaList: [],
        odcAmount: 0,
        consumptionAmount: 0,
        consumptionExceedsPo: false
      },
      // Set only for campaigns run through an exchange (Magnite / Place Exchange): { id, name }
      responsibleAgency: null,
      // Holder company (id + name) shown in the "Empresa" field; billing_information stays as the relation
      holderCompany: { id: null, name: '' },
      formData: {
        number: null,
        billing_information_id: null,
        company_fantasy_name: '',
        company_legal_name: null,
        billing_mode: '',
        payment_mode: '',
        payment_term: 30,
        charge_term: 60,
        date_from: '',
        date_to: '',
        amount: 0,
        currency: 'USD',
        agency_commission_mode: '',
        agency_commission_percentage: 0,
        agency_commission_currency: 'USD',
        agency_commission_amount: 0,
        exchange_commission_percentage: 0,
        exchange_commission_currency: 'USD',
        exchange_commission_amount: 0,
        international_tax_percentage: 0,
        international_tax_currency: 'USD',
        international_tax_amount: 0,
        national_tax_percentage: 0,
        national_tax_currency: 'USD',
        national_tax_amount: 0,
        calculation_base: 0,
        // WHT adjustment: standalone calculator, unrelated to calculation_base
        wht_calculation_base: 0,
        closure_observation: '',
        billing_comment: '',
        status: CLOSURE_STATUS.DRAFT
      },
      // Field suggestion tracking: controls which fields show green (suggested) or orange (default) borders
      fieldStates: {
        company_fantasy_name: { isSuggested: false, isDefault: false, edited: false },
        billing_information_id: { isSuggested: false, isDefault: false, edited: false },
        billing_mode: { isSuggested: false, isDefault: false, edited: false },
        payment_mode: { isSuggested: false, isDefault: false, edited: false },
        payment_term: { isSuggested: false, isDefault: false, edited: false },
        charge_term: { isSuggested: false, isDefault: false, edited: false },
        date_from: { isSuggested: false, isDefault: false, edited: false },
        date_to: { isSuggested: false, isDefault: false, edited: false },
        amount: { isSuggested: false, isDefault: false, edited: false },
        currency: { isSuggested: false, isDefault: false, edited: false },
        agency_commission_mode: { isSuggested: false, isDefault: false, edited: false },
        agency_commission_percentage: { isSuggested: false, isDefault: false, edited: false },
        agency_commission_currency: { isSuggested: false, isDefault: false, edited: false },
        exchange_commission_percentage: { isSuggested: false, isDefault: false, edited: false },
        exchange_commission_currency: { isSuggested: false, isDefault: false, edited: false },
        // The WHT adjustment fields have no suggestion state: they are user-driven and start at zero
        national_tax_percentage: { isSuggested: false, isDefault: false, edited: false },
        national_tax_amount: { isSuggested: false, isDefault: false, edited: false },
        national_tax_currency: { isSuggested: false, isDefault: false, edited: false },
        calculation_base: { isSuggested: false, isDefault: false, edited: false }
      },
      // Constants for template
      CLOSURE_STATUS,
      BILLING_MODES_LABELS,
      PAYMENT_MODES_LABELS,
      AGENCY_COMMISSION_MODES_LABELS,
      dateFormatter,
      stringCurrencyFormatter
    }
  },
  computed: {
    formTitle() {
      return this.closure ? `Editar cierre #${this.getClosureDisplayNumber(this.closure)}` : 'Crear nuevo cierre de ventas'
    },
    isEditMode() {
      return !!this.closure
    },
    isEditingValidClosure() {
      return this.isEditMode && this.closure && this.closure.status === CLOSURE_STATUS.VALID
    },
    canSaveAsDraft() {
      return !this.isEditingValidClosure
    },
    saveChoiceDescription() {
      if (this.isEditingValidClosure) {
        return 'El cierre actual se anulará y se creará otro con los datos actualizados.'
      }

      return 'Seleccione cómo desea guardar este cierre.'
    },
    billingModeOptions() {
      return Object.entries(this.BILLING_MODES_LABELS).map(([value, label]) => ({ value, label }))
    },
    paymentModeOptions() {
      return Object.entries(this.PAYMENT_MODES_LABELS).map(([value, label]) => ({ value, label }))
    },
    agencyCommissionModeOptions() {
      return Object.entries(this.AGENCY_COMMISSION_MODES_LABELS).map(([value, label]) => ({ value, label }))
    },
    currencyOptions() {
      return this.availableCurrencies.slice()
    },
    summaryCurrency() {
      return this.campaignBaseData.currency || this.formData.currency || 'USD'
    },
    isConsumptionExceeded() {
      // The backend derives it from the campaign purchase orders, converted to a single currency
      return this.campaignBaseData.consumptionExceedsPo === true
    },
    consumptionExceededMessage() {
      const consumption = this.parseNumber(this.campaignBaseData.consumptionAmount) || 0

      return `El consumo de esta campaña supera el monto de la ODC. Consumo: ${this.stringCurrencyFormatter(consumption)} ${this.summaryCurrency}`
    },
    campaignTypeLabel() {
      const labels = {
        traditional: 'Tradicional',
        programmatic: 'Programática',
        programmatic_guaranteed: 'Programática garantizada'
      }

      return labels[this.campaignBaseData.campaignType] || 'No informado'
    },
    campaignDateRangeText() {
      if (!this.campaignBaseData.startDate || !this.campaignBaseData.endDate) {
        return 'No informado'
      }

      return `${this.dateFormatter(this.campaignBaseData.startDate)} a ${this.dateFormatter(this.campaignBaseData.endDate)}`
    },
    selectedCompanyName() {
      const selectedCompany = this.availableCompanies.find((company) => {
        const companyBillingInfoId = company.billing_information_id || company.id
        return String(companyBillingInfoId) === String(this.formData.billing_information_id)
      })

      return (selectedCompany && selectedCompany.name) || ''
    },
    selectedCompanyId() {
      const selectedCompany = this.availableCompanies.find((company) => {
        const companyBillingInfoId = company.billing_information_id || company.id
        return String(companyBillingInfoId) === String(this.formData.billing_information_id)
      })

      return (selectedCompany && selectedCompany.id) || null
    },
    companyFantasyNameWithId() {
      // Show the company name and its id; falls back to the fantasy name when no company id is present
      const name = this.holderCompany.name || this.formData.company_fantasy_name
      if (name && this.holderCompany.id) {
        return `${name} (${this.holderCompany.id})`
      }
      return name
    },
    responsibleAgencyLabel() {
      if (!this.responsibleAgency) return ''

      const { name, id } = this.responsibleAgency
      return id ? `${name} (${id})` : name
    },
    closureSummary() {
      // Computed locally rather than from liveCalculation: the backend only recalculates on a
      // billing mode, payment mode or date change, so its values are null or stale for every
      // other edit. Both formulas are the same, so this preview matches the persisted amounts.
      const netAmount = calculateNetAmount(this.formData)
      const billingAmount = calculateBillingAmount(this.formData)

      return {
        agency_payment: this.formData.amount,
        agency_commission_payment: this.formData.agency_commission_amount,
        exchange_commission_payment: this.formData.exchange_commission_amount,
        media_payment: this.formData.calculation_base,
        net_amount: netAmount,
        international_tax_payment: this.formData.international_tax_amount,
        national_tax_payment: this.formData.national_tax_amount,
        billing_amount: billingAmount,
        currency: this.formData.currency
      }
    }
  },
  watch: {
    active: {
      handler(newVal) {
        if (newVal) {
          this.initializeAndOpen()
          return
        }

        this.isOpen = false
      },
      immediate: false
    }
  },
  methods: {
    async initializeAndOpen() {
      if (this.isLoading) return

      this.$emit('loading-change', true)
      await this.initializeForm()

      if (this.active) {
        this.isOpen = true
      }

      this.$emit('loading-change', false)
    },

    getInitialFormData() {
      return {
        number: null,
        billing_information_id: null,
        company_fantasy_name: '',
        company_legal_name: null,
        billing_mode: '',
        payment_mode: '',
        payment_term: 30,
        charge_term: 60,
        date_from: '',
        date_to: '',
        amount: 0,
        currency: 'USD',
        agency_commission_mode: '',
        agency_commission_percentage: 0,
        agency_commission_currency: 'USD',
        agency_commission_amount: 0,
        exchange_commission_percentage: 0,
        exchange_commission_currency: 'USD',
        exchange_commission_amount: 0,
        international_tax_percentage: 0,
        international_tax_currency: 'USD',
        international_tax_amount: 0,
        national_tax_percentage: 0,
        national_tax_currency: 'USD',
        national_tax_amount: 0,
        calculation_base: 0,
        // WHT adjustment: standalone calculator, unrelated to calculation_base
        wht_calculation_base: 0,
        closure_observation: '',
        billing_comment: '',
        status: CLOSURE_STATUS.DRAFT
      }
    },

    resetFormData() {
      this.formData = this.getInitialFormData()
      this.liveCalculation = null
      this.dateRangeAlerts = {
        date_from: '',
        date_to: ''
      }
    },

    /**
     * Initialize form with data
     */
    async initializeForm() {
      this.isLoading = true
      this.validationErrors = []
      this.erroredFields = []
      this.responsibleAgency = null
      this.holderCompany = { id: null, name: '' }
      this.resetFormData()
      this.isFantasyNameFixed = false
      // Collapsed on every open; populateFormWithClosure reopens it when the closure carries WHT
      this.isWhtSectionOpen = false

      try {
        await this.fetchModalData()

        if (!this.campaignBaseData.startDate || !this.campaignBaseData.endDate) {
          await this.fetchCampaignDates()
        }

        if (this.formData.currency && !this.availableCurrencies.includes(this.formData.currency)) {
          this.availableCurrencies = [...this.availableCurrencies, this.formData.currency].sort()
        }

        // If editing, populate form with closure data
        if (this.isEditMode) {
          this.populateFormWithClosure()
        } else {
          await this.suggestDefaults()
        }
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: 'Error al cargar el formulario: ' + (error.message || 'Unknown error'),
          color: 'danger'
        })
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Fetch modal bootstrap data from backend
     */
    async fetchModalData() {
      try {
        const response = await this.$http.get(
          `${appConfig.apiUrl}/campaigns/${this.campaignId}/sales-closures/modal-data`
        )

        if (!response) {
          throw new Error('No response from API')
        }

        const payload = (response && response.data && response.data.data) || response.data || {}
        const budgetSummary = payload.budget_summary || {}
        const company = payload.company || {}
        const companyDefaults = company.defaults || {}

        const summaryCurrency = budgetSummary.currency || this.formData.currency || 'USD'

        this.campaignBaseData = {
          ...this.campaignBaseData,
          campaignId: payload.campaign_id || null,
          campaignName: payload.campaign_name || '',
          budgetTotal: this.parseNumber(budgetSummary.agency_budget) || 0,
          consumptionReal: this.parseNumber(budgetSummary.agency_real) || 0,
          mediaOwnerBudget: this.parseNumber(budgetSummary.media_budget) || 0,
          mediaOwnerConsumption: this.parseNumber(budgetSummary.media_real) || 0,
          currency: summaryCurrency,
          campaignType: payload.campaign_type || this.campaignBaseData.campaignType,
          startDate: this.normalizeApiDate(payload.date_from || this.campaignBaseData.startDate),
          endDate: this.normalizeApiDate(payload.date_to || this.campaignBaseData.endDate),
          exchangeName: payload.exchange_name || '',
          dspName: payload.dsp_name || '',
          comments: payload.comments || '',
          mediaCount: payload.media_count || 0,
          mediaList: payload.media_list || [],
          odcAmount: this.parseNumber(payload.odc_amount) || 0,
          consumptionAmount: this.parseNumber(payload.consumption_amount) || 0,
          consumptionExceedsPo: payload.consumption_exceeds_po === true
        }

        const billingInformations = Array.isArray(company.billing_informations) ? company.billing_informations : []
        this.availableCompanies = billingInformations
          .map((billingInformation) => ({
            ...billingInformation,
            id: billingInformation.id,
            billing_information_id: billingInformation.id,
            name: billingInformation.name
          }))
          .filter((billingInformation) => billingInformation.id && billingInformation.name)

        // Company (the billing holder): its id and name are shown in the "Empresa" field,
        // separate from billing_information which stays as the actual relation
        this.holderCompany = { id: company.id || null, name: company.name || '' }

        // For exchange campaigns the holder comes as company.name (Magnite) without a fantasy_name
        const companyDisplayName = company.fantasy_name || company.name
        if (companyDisplayName) {
          this.formData.company_fantasy_name = companyDisplayName
          this.markFieldAsSuggested('company_fantasy_name')
          this.isFantasyNameFixed = true
        }

        // Present only when the campaign ran through an exchange; null hides the block
        this.responsibleAgency = payload.responsible_agency || null

        if (summaryCurrency && !this.availableCurrencies.includes(summaryCurrency)) {
          this.availableCurrencies = [...this.availableCurrencies, summaryCurrency].sort()
        }

        // Store company defaults for create mode initialization
        this.formData.billing_mode = companyDefaults.billing_mode || this.formData.billing_mode
        this.formData.payment_mode = companyDefaults.payment_mode || this.formData.payment_mode
        this.formData.charge_term =
          this.parseNumber(companyDefaults.charge_term) !== null
            ? this.parseNumber(companyDefaults.charge_term)
            : this.formData.charge_term
        this.formData.payment_term =
          this.parseNumber(companyDefaults.payment_term) !== null
            ? this.parseNumber(companyDefaults.payment_term)
            : this.formData.payment_term
        this.formData.agency_commission_mode =
          companyDefaults.agency_commission_mode || this.formData.agency_commission_mode

        const agencyCommissionPercentage = this.parseNumber(companyDefaults.agency_commission_percentage)
        if (agencyCommissionPercentage !== null) {
          this.formData.agency_commission_percentage = agencyCommissionPercentage
        }

        const exchangeCommissionPercentage = this.parseNumber(companyDefaults.exchange_commission_percentage)
        if (exchangeCommissionPercentage !== null) {
          this.formData.exchange_commission_percentage = exchangeCommissionPercentage
        }

        // The WHT adjustment is an opt-in calculator: its base, percentage and amount always
        // start at zero, so no company default is applied to them (only the shared currency).

        const nationalTaxPercentage = this.parseNumber(companyDefaults.national_tax_percentage)
        if (nationalTaxPercentage !== null) {
          this.formData.national_tax_percentage = nationalTaxPercentage
        }

        this.formData.agency_commission_currency =
          companyDefaults.agency_commission_currency ||
          this.formData.agency_commission_currency ||
          summaryCurrency

        this.formData.exchange_commission_currency =
          companyDefaults.exchange_commission_currency ||
          this.formData.exchange_commission_currency ||
          summaryCurrency

        this.formData.international_tax_currency =
          companyDefaults.international_tax_currency ||
          this.formData.international_tax_currency ||
          summaryCurrency

        this.formData.national_tax_currency =
          companyDefaults.national_tax_currency ||
          this.formData.national_tax_currency ||
          summaryCurrency

        this.formData.currency = summaryCurrency

        // Mark fields based on whether they have real data or are defaults
        if (companyDefaults.billing_mode) {
          this.markFieldAsSuggested('billing_mode')
        } else {
          this.markFieldAsDefault('billing_mode')
        }

        if (companyDefaults.payment_mode) {
          this.markFieldAsSuggested('payment_mode')
        } else {
          this.markFieldAsDefault('payment_mode')
        }

        if (this.parseNumber(companyDefaults.charge_term) !== null) {
          this.markFieldAsSuggested('charge_term')
        } else {
          this.markFieldAsDefault('charge_term')
        }

        if (this.parseNumber(companyDefaults.payment_term) !== null) {
          this.markFieldAsSuggested('payment_term')
        } else {
          this.markFieldAsDefault('payment_term')
        }

        if (companyDefaults.agency_commission_mode) {
          this.markFieldAsSuggested('agency_commission_mode')
        } else {
          this.markFieldAsDefault('agency_commission_mode')
        }

        if (this.parseNumber(companyDefaults.agency_commission_percentage) !== null) {
          this.markFieldAsSuggested('agency_commission_percentage')
        } else {
          this.markFieldAsDefault('agency_commission_percentage')
        }

        if (this.parseNumber(companyDefaults.exchange_commission_percentage) !== null) {
          this.markFieldAsSuggested('exchange_commission_percentage')
        } else {
          this.markFieldAsDefault('exchange_commission_percentage')
        }

        if (this.parseNumber(companyDefaults.national_tax_percentage) !== null) {
          this.markFieldAsSuggested('national_tax_percentage')
        } else {
          this.markFieldAsDefault('national_tax_percentage')
        }

        if (companyDefaults.agency_commission_currency) {
          this.markFieldAsSuggested('agency_commission_currency')
        } else {
          this.markFieldAsDefault('agency_commission_currency')
        }

        if (companyDefaults.exchange_commission_currency) {
          this.markFieldAsSuggested('exchange_commission_currency')
        } else {
          this.markFieldAsDefault('exchange_commission_currency')
        }

        if (companyDefaults.national_tax_currency) {
          this.markFieldAsSuggested('national_tax_currency')
        } else {
          this.markFieldAsDefault('national_tax_currency')
        }

        this.markFieldAsSuggested('currency')

        const defaultAmount = this.parseNumber(payload.default_amount)
        if (defaultAmount !== null && !this.isEditMode) {
          this.formData.amount = defaultAmount
          this.markFieldAsSuggested('amount')
        } else {
          this.markFieldAsDefault('amount')
        }

        const calculationBase = this.parseNumber(payload.calculation_base)
        if (calculationBase !== null && !this.isEditMode) {
          this.formData.calculation_base = calculationBase
          this.markFieldAsSuggested('calculation_base')
        } else {
          this.markFieldAsDefault('calculation_base')
        }

        if (!this.formData.billing_information_id && this.availableCompanies.length === 1) {
          this.formData.billing_information_id = this.availableCompanies[0].billing_information_id
          this.onBillingInformationChange(this.formData.billing_information_id)
          this.markFieldAsSuggested('billing_information_id')
        } else if (this.formData.billing_information_id) {
          this.markFieldAsSuggested('billing_information_id')
        }
      } catch (error) {
        // Continue with defaults even if fetch fails
        this.formData.billing_mode = this.formData.billing_mode || 'full_campaign_start_100'
        this.formData.payment_mode = this.formData.payment_mode || 'always_pay_full_po'
        this.formData.agency_commission_mode = this.formData.agency_commission_mode || 'platform_invoice_payment'
        throw error
      }
    },

    /**
     * Fetch campaign date bounds used by billing date suggestion rules
     */
    async fetchCampaignDates() {
      try {
        const response = await this.$http.get(`${appConfig.apiUrl}/campaigns/${this.campaignId}`)

        if (!response) {
          return
        }

        const data = response.data || {}

        const startDate =
          data.start_date ||
          data.date_from ||
          data.started_at ||
          data.start_at ||
          ''

        const endDate =
          data.end_date ||
          data.date_to ||
          data.ended_at ||
          data.end_at ||
          ''

        this.campaignBaseData = {
          ...this.campaignBaseData,
          startDate: this.normalizeApiDate(startDate),
          endDate: this.normalizeApiDate(endDate)
        }
      } catch (error) {
        // Don't throw - this is optional data
      }
    },

    parseNumber(value) {
      if (value === null || value === undefined || value === '') return null
      if (typeof value === 'number') return Number.isFinite(value) ? value : null

      const normalized = String(value).replace(',', '.').trim()
      const parsed = Number(normalized)
      return Number.isFinite(parsed) ? parsed : null
    },

    firstNumber(...values) {
      for (const value of values) {
        const parsed = this.parseNumber(value)
        if (parsed !== null) return parsed
      }

      return 0
    },

    normalizeApiDate(value) {
      if (!value) return ''

      const rawValue = String(value).trim()
      const firstToken = rawValue.split(' ')[0]
      const dateToken = firstToken.split('T')[0]

      if (/^\d{4}-\d{2}-\d{2}$/.test(dateToken)) {
        return dateToken
      }

      return ''
    },

    /**
     * Populate form with existing closure data
     */
    populateFormWithClosure() {
      const closureFantasyName =
        (this.closure && this.closure.company_fantasy_name) ||
        (this.closure && this.closure.fantasy_name) ||
        (this.closure && this.closure.company && this.closure.company.fantasy_name) ||
        this.formData.company_fantasy_name ||
        ''

      const closureLegalName =
        (this.closure && this.closure.company_legal_name) ||
        (this.closure && this.closure.legal_name) ||
        (this.closure && this.closure.billing_information_name) ||
        (this.closure && this.closure.billing_information && this.closure.billing_information.name) ||
        this.formData.company_legal_name ||
        null

      this.formData = {
        ...this.getInitialFormData(),
        ...this.closure,
        billing_information_id:
          (this.closure && this.closure.billing_information_id) ||
          (this.closure && this.closure.billing_info_id) ||
          null,
        company_fantasy_name: closureFantasyName,
        company_legal_name: closureLegalName,
        date_from: this.normalizeApiDate(this.closure && this.closure.date_from),
        date_to: this.normalizeApiDate(this.closure && this.closure.date_to)
      }

      this.onBillingInformationChange(this.formData.billing_information_id)

      // Editing a closure that already carries a WHT adjustment opens the section.
      // Note the values are used as persisted: they are never recalculated on load, because
      // closures created before wht_calculation_base existed have an amount but no base.
      this.isWhtSectionOpen = this.hasWhtValues()
    },

    /**
     * Whether the closure carries a WHT adjustment. Any of the three values counts: legacy
     * closures hold an international tax amount without a base.
     */
    hasWhtValues() {
      const whtFields = ['wht_calculation_base', 'international_tax_percentage', 'international_tax_amount']

      return whtFields.some((field) => (this.parseNumber(this.formData[field]) || 0) > 0)
    },

    /**
     * Suggest default values based on billing mode
     */
    async suggestDefaults() {
      this.formData.billing_mode = this.formData.billing_mode || 'full_campaign_start_100'
      this.formData.payment_mode = this.formData.payment_mode || 'always_pay_full_po'
      this.formData.agency_commission_mode = this.formData.agency_commission_mode || 'platform_invoice_payment'

      // Dates will be marked as suggested after they are generated in onBillingModeChange()
      await this.onBillingModeChange()
    },

    /**
     * Handle billing mode change - update suggested dates and amounts
     */
    async onBillingModeChange() {
      if (!this.formData.billing_mode) return

      const billingModeToPaymentMode = {
        full_campaign_start_100: 'always_pay_full_po',
        campaign_start_partial_months: 'always_pay_full_po',
        campaign_completed_no_monthly_break: 'pay_by_consumption',
        monthly_closure: 'pay_by_consumption'
      }

      const mappedPaymentMode = billingModeToPaymentMode[this.formData.billing_mode]
      if (mappedPaymentMode) {
        this.formData.payment_mode = mappedPaymentMode
        // Mark payment mode as suggested when auto-updated from billing mode mapping
        this.markFieldAsSuggested('payment_mode')
      }

      const campaignStartDate = this.campaignBaseData.startDate || '2026-01-01'
      const campaignEndDate = this.campaignBaseData.endDate || '2026-12-31'

      // For now, use placeholder dates - in real scenario would fetch from campaign
      const dateRange = generateSuggestedDateRange(
        this.formData.billing_mode,
        {
          start_date: campaignStartDate,
          end_date: campaignEndDate
        },
        this.existingClosures
      )

      if (dateRange.dateFrom && dateRange.dateTo) {
        this.formData.date_from = dateRange.dateFrom
        this.formData.date_to = dateRange.dateTo
        // Mark dates as suggested when auto-calculated from billing mode
        this.markFieldAsSuggested('date_from')
        this.markFieldAsSuggested('date_to')
      }

      await this.updateSuggestedAmount()
    },

    /**
     * Handle payment mode change - recalculate suggested amount
     */
    async onPaymentModeChange() {
      await this.updateSuggestedAmount()
    },

    /**
     * Handle date range change - update suggested amount
     */
    async onDateRangeChange() {
      if (this.formData.date_from && this.formData.date_to && this.formData.date_from > this.formData.date_to) {
        this.formData.date_to = this.formData.date_from
      }

      this.validateDateRangeAgainstCampaign()
      await this.updateSuggestedAmount()
      this.validateDateOverlap()
    },

    handleDateFromChange() {
      this.markFieldAsEdited('date_from')
      this.onDateRangeChange()
    },

    handleDateToChange() {
      this.markFieldAsEdited('date_to')
      this.onDateRangeChange()
    },

    /**
     * Handle amount manual change - update commission amounts
     */
    onAmountChange() {
      this.sanitizeNonNegativeField('amount')
      this.updateCommissionAmounts()
    },

    toggleWhtSection() {
      this.isWhtSectionOpen = !this.isWhtSectionOpen
    },

    onWhtBaseChange() {
      this.sanitizeNonNegativeField('wht_calculation_base')
      this.recalculateWhtAmount()
    },

    onWhtPercentageChange() {
      this.sanitizeNonNegativeField('international_tax_percentage')
      // Only clears the validation outline: this field has no suggestion state
      this.markFieldAsEdited('international_tax_percentage')
      this.recalculateWhtAmount()
    },

    onWhtAmountChange() {
      this.sanitizeNonNegativeField('international_tax_amount')
      this.recalculateWhtPercentage()
    },

    /**
     * WHT adjustment: standalone calculator over its own base. Percentage and amount are two
     * views of the same value, so editing one re-derives the other from the base.
     * Never reacts to the closure amount, the dates, the payment mode or the other commissions.
     */
    recalculateWhtAmount() {
      const whtBase = parseFloat(this.formData.wht_calculation_base) || 0

      this.formData.international_tax_amount = parseFloat(
        calculateCommissionAmount(whtBase, this.formData.international_tax_percentage).toFixed(2)
      )
    },

    recalculateWhtPercentage() {
      const whtBase = parseFloat(this.formData.wht_calculation_base) || 0

      // Without a base there is nothing to derive: keep the percentage until one is entered
      if (whtBase <= 0) return

      const whtAmount = parseFloat(this.formData.international_tax_amount) || 0

      // More precision than the amount, so editing one and then the other does not drift
      this.formData.international_tax_percentage = parseFloat(
        ((whtAmount / whtBase) * 100).toFixed(6)
      )
    },

    onCommissionAmountChange(fieldName) {
      // Editar el monto sin afectar el porcentaje
      this.sanitizeNonNegativeField(fieldName)
    },

    onIntegerNonNegativeInput(fieldName) {
      this.sanitizeNonNegativeField(fieldName, { integer: true })
    },

    onCommissionPercentageChange(fieldName) {
      this.sanitizeNonNegativeField(fieldName)
      this.updateCommissionAmounts()
    },

    sanitizeNonNegativeField(fieldName, options = {}) {
      const { integer = false } = options
      const rawValue = this.formData[fieldName]

      if (rawValue === null || rawValue === undefined || rawValue === '') {
        this.formData[fieldName] = 0
        return
      }

      const parsed = integer ? parseInt(rawValue, 10) : parseFloat(rawValue)
      if (Number.isNaN(parsed)) {
        this.formData[fieldName] = 0
        return
      }

      const normalized = Math.max(0, parsed)
      this.formData[fieldName] = integer ? Math.trunc(normalized) : normalized
    },

    getClosureDisplayNumber(closure) {
      return (closure && (closure.number || closure.closure_number || closure.id)) || '-'
    },

    onBillingInformationChange(selectedBillingInformationId) {
      const selectedCompany = this.availableCompanies.find((company) => {
        const companyBillingInfoId = company.billing_information_id || company.id
        return String(companyBillingInfoId) === String(selectedBillingInformationId)
      })

      if (selectedCompany) {
        this.formData.company_legal_name = selectedCompany.name
        this.formData.billing_information_id = selectedCompany.billing_information_id || selectedCompany.id
      }
    },

    companyFilterBy(option, label, search) {
      const normalizedLabel = String(label || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()

      const normalizedSearch = String(search || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()

      return normalizedLabel.includes(normalizedSearch)
    },

    getFormDateRange() {
      const dateFrom = this.normalizeApiDate(this.formData.date_from)
      const dateTo = this.normalizeApiDate(this.formData.date_to)

      if (!dateFrom || !dateTo) return null
      if (dateFrom > dateTo) return null

      return {
        date_from: dateFrom,
        date_to: dateTo
      }
    },

    /**
     * Update suggested amount based on selected billing and payment modes
     */
    async updateSuggestedAmount() {
      if (!this.formData.billing_mode || !this.formData.payment_mode) return

      const dateRange = this.getFormDateRange()
      if (!dateRange) return

      const requestToken = ++this.amountRequestToken
      this.isAmountLoading = true

      try {
        const response = await this.$http.get(
          `${appConfig.apiUrl}/campaigns/${this.campaignId}/sales-closures/amount-calculation`,
          {
            params: {
              ...dateRange,
              payment_mode: this.formData.payment_mode,
              // Sent so the billing_amount returned by the API includes the WHT adjustment.
              // The WHT fields in the response are ignored: this block is edited only by the user.
              wht_calculation_base: this.parseNumber(this.formData.wht_calculation_base) || 0,
              international_tax_percentage: this.parseNumber(this.formData.international_tax_percentage) || 0
            }
          }
        )

        const payload = (response && response.data && response.data.data) || response.data || {}

        // Ignore stale responses when user changes inputs quickly
        if (requestToken !== this.amountRequestToken) {
          return
        }

        const amountFromApi = this.parseNumber(payload.amount)
        const calculationBaseFromApi = this.parseNumber(payload.calculation_base)
        const baseAmountFromApi = this.parseNumber(payload.base_amount)
        const mediaAmountFromApi = this.parseNumber(payload.media_amount)

        const agencyCommissionPercentageFromApi = this.parseNumber(payload.agency_commission_percentage)
        const exchangeCommissionPercentageFromApi = this.parseNumber(payload.exchange_commission_percentage)
        const nationalTaxPercentageFromApi = this.parseNumber(payload.national_tax_percentage)

        const agencyCommissionAmountFromApi = this.parseNumber(payload.agency_commission_amount)
        const exchangeCommissionAmountFromApi = this.parseNumber(payload.exchange_commission_amount)
        const nationalTaxAmountFromApi = this.parseNumber(payload.national_tax_amount)

        const netAmountFromApi = this.parseNumber(payload.net_amount)
        const billingAmountFromApi = this.parseNumber(payload.billing_amount)

        if (amountFromApi !== null) {
          this.formData.amount = amountFromApi
          // Mark amount as suggested when updated from API
          this.markFieldAsSuggested('amount')
        }

        if (calculationBaseFromApi !== null) {
          this.formData.calculation_base = calculationBaseFromApi
          this.markFieldAsSuggested('calculation_base')
        } else if (baseAmountFromApi !== null) {
          this.formData.calculation_base = baseAmountFromApi
          this.markFieldAsSuggested('calculation_base')
        } else if (mediaAmountFromApi !== null) {
          this.formData.calculation_base = mediaAmountFromApi
          this.markFieldAsSuggested('calculation_base')
        } else {
          this.formData.calculation_base = this.suggestCalculationBase()
        }

        if (agencyCommissionPercentageFromApi !== null) {
          this.formData.agency_commission_percentage = agencyCommissionPercentageFromApi
        }

        if (exchangeCommissionPercentageFromApi !== null) {
          this.formData.exchange_commission_percentage = exchangeCommissionPercentageFromApi
        }

        // The API always returns the country default, so it must not override an edited value
        if (nationalTaxPercentageFromApi !== null && !this.fieldStates.national_tax_percentage.edited) {
          this.formData.national_tax_percentage = nationalTaxPercentageFromApi
          this.markFieldAsSuggested('national_tax_percentage')
        }

        if (payload.agency_commission_currency) {
          this.formData.agency_commission_currency = payload.agency_commission_currency
        }

        if (payload.exchange_commission_currency) {
          this.formData.exchange_commission_currency = payload.exchange_commission_currency
        }

        if (payload.national_tax_currency) {
          this.formData.national_tax_currency = payload.national_tax_currency
        }

        if (agencyCommissionAmountFromApi !== null) {
          this.formData.agency_commission_amount = agencyCommissionAmountFromApi
        }

        if (exchangeCommissionAmountFromApi !== null) {
          this.formData.exchange_commission_amount = exchangeCommissionAmountFromApi
        }

        if (nationalTaxAmountFromApi !== null && !this.fieldStates.national_tax_amount.edited) {
          this.formData.national_tax_amount = nationalTaxAmountFromApi
          this.markFieldAsSuggested('national_tax_amount')
        }

        if (payload.currency) {
          this.formData.currency = payload.currency
          if (!this.availableCurrencies.includes(payload.currency)) {
            this.availableCurrencies = [...this.availableCurrencies, payload.currency].sort()
          }
        }

        if (netAmountFromApi !== null || billingAmountFromApi !== null) {
          this.liveCalculation = {
            net_amount: netAmountFromApi,
            billing_amount: billingAmountFromApi,
            currency: payload.currency || this.formData.currency
          }
        }

        // Purchase order data drives the consumption warning next to the amount input
        this.campaignBaseData = {
          ...this.campaignBaseData,
          odcAmount: this.parseNumber(payload.odc_amount) || this.campaignBaseData.odcAmount,
          consumptionAmount: this.parseNumber(payload.consumption_amount) || this.campaignBaseData.consumptionAmount,
          consumptionExceedsPo: payload.consumption_exceeds_po === true
        }
      } catch (error) {
        if (requestToken !== this.amountRequestToken) {
          return
        }

        this.liveCalculation = null

        const dateRange = this.getFormDateRange()
        if (!dateRange) return

        const suggestedAmount = calculateSuggestedAmount(
          this.formData.billing_mode,
          this.formData.payment_mode,
          {
            dateFrom: dateRange.date_from,
            dateTo: dateRange.date_to
          },
          this.campaignBaseData
        )

        this.formData.amount = suggestedAmount
        this.markFieldAsSuggested('amount')

        this.formData.calculation_base = this.suggestCalculationBase()
        this.markFieldAsSuggested('calculation_base')
      } finally {
        if (requestToken === this.amountRequestToken) {
          this.isAmountLoading = false
          // Only update agency and exchange commissions, NOT international tax
          this.updateAgencyAndExchangeCommissions()
        }
      }
    },

    /**
     * Update only agency and exchange commissions (not international tax)
     */
    updateAgencyAndExchangeCommissions() {
      const amountBase = parseFloat(this.formData.amount) || 0

      this.formData.agency_commission_amount = parseFloat(
        calculateCommissionAmount(amountBase, this.formData.agency_commission_percentage).toFixed(2)
      )

      this.formData.exchange_commission_amount = parseFloat(
        calculateCommissionAmount(amountBase, this.formData.exchange_commission_percentage).toFixed(2)
      )
    },

    suggestCalculationBase() {
      const isFullODC =
        this.formData.payment_mode === 'always_pay_full_po' ||
        this.formData.payment_mode === 'pay_full_odc'

      const suggested = isFullODC
        ? this.campaignBaseData.mediaOwnerBudget
        : this.campaignBaseData.mediaOwnerConsumption

      return this.parseNumber(suggested) || 0
    },

    /**
     * Update all commission amounts based on percentage
     */
    updateCommissionAmounts() {
      const amountBase = parseFloat(this.formData.amount) || 0

      // The backend calculation no longer matches the edited values, recompute the summary locally
      this.liveCalculation = null

      this.formData.agency_commission_amount = parseFloat(
        calculateCommissionAmount(amountBase, this.formData.agency_commission_percentage).toFixed(2)
      )

      this.formData.exchange_commission_amount = parseFloat(
        calculateCommissionAmount(amountBase, this.formData.exchange_commission_percentage).toFixed(2)
      )

      // Only the monto typed in by hand survives a recalculation
      const nationalTaxFieldState = this.fieldStates.national_tax_amount
      const nationalTaxEdited = nationalTaxFieldState && nationalTaxFieldState.edited

      if (!nationalTaxEdited) {
        this.formData.national_tax_amount = parseFloat(
          calculateCommissionAmount(amountBase, this.formData.national_tax_percentage).toFixed(2)
        )
      }

      // The WHT adjustment is deliberately left out: it is a standalone calculator (see recalculateWhtAmount)
    },

    /**
     * Validate that dates don't overlap with existing closures
     */
    validateDateOverlap() {
      if (!this.formData.date_from || !this.formData.date_to) return

      const validation = validateNoDateOverlap(
        this.existingClosures,
        {
          date_from: this.formData.date_from,
          date_to: this.formData.date_to
        },
        (this.closure && this.closure.id)
      )

      if (!validation.isValid) {
        // Only add this error: the rest of the list belongs to the other validations
        this.addValidationError(validation.message, 'date_from', 'date_to')
        return
      }

      this.clearValidationError(OVERLAP_ERROR_MESSAGE, 'date_from', 'date_to')
    },

    /**
     * Drop a single error and unmark its fields, leaving the other validations untouched
     */
    clearValidationError(message, ...fieldNames) {
      this.validationErrors = this.validationErrors.filter(error => error !== message)
      this.erroredFields = this.erroredFields.filter(field => !fieldNames.includes(field))
    },

    /**
     * Validate that selected dates are within campaign period - shows warning but doesn't block
     */
    validateDateRangeAgainstCampaign() {
      this.dateRangeAlerts.date_from = ''
      this.dateRangeAlerts.date_to = ''

      if (!this.formData.date_from || !this.formData.date_to) return
      if (!this.campaignBaseData.startDate || !this.campaignBaseData.endDate) return

      const campaignStart = this.campaignBaseData.startDate
      const campaignEnd = this.campaignBaseData.endDate
      const closureStart = this.formData.date_from
      const closureEnd = this.formData.date_to

      if (closureStart < campaignStart) {
        this.dateRangeAlerts.date_from = 'Fuera del período de la campaña'
      }

      if (closureEnd > campaignEnd) {
        this.dateRangeAlerts.date_to = 'Fuera del período de la campaña'
      }
    },

    /**
     * Validate form before submission
     */
    validateForm(targetStatus) {
      this.validationErrors = []
      this.erroredFields = []

      if (!this.formData.billing_information_id) {
        this.addValidationError('Debe seleccionar una razón social', 'billing_information_id')
      }

      if (!this.formData.company_fantasy_name) {
        this.addValidationError('Debe ingresar un nombre fantasía', 'company_fantasy_name')
      }

      if (!this.formData.billing_mode) {
        this.addValidationError('Debe seleccionar un modo de facturación', 'billing_mode')
      }

      if (!this.formData.payment_mode) {
        this.addValidationError('Debe seleccionar un modo de pago', 'payment_mode')
      }

      if (!this.formData.date_from || !this.formData.date_to) {
        this.addValidationError('Debe seleccionar las fechas del cierre', 'date_from', 'date_to')
      }

      if ((parseInt(this.formData.payment_term, 10) || 0) < 0) {
        this.addValidationError('El plazo de pago no puede ser negativo', 'payment_term')
      }

      if ((parseInt(this.formData.charge_term, 10) || 0) < 0) {
        this.addValidationError('El plazo de cobro no puede ser negativo', 'charge_term')
      }

      if ((parseFloat(this.formData.amount) || 0) < 0) {
        this.addValidationError('El monto no puede ser negativo', 'amount')
      }

      if ((parseFloat(this.formData.calculation_base) || 0) < 0) {
        this.addValidationError('La base de cálculo no puede ser negativa', 'calculation_base')
      }

      if ((parseFloat(this.formData.agency_commission_percentage) || 0) < 0) {
        this.addValidationError('La comisión de agencia no puede ser negativa', 'agency_commission_percentage')
      }

      if ((parseFloat(this.formData.exchange_commission_percentage) || 0) < 0) {
        this.addValidationError('La comisión de exchange no puede ser negativa', 'exchange_commission_percentage')
      }

      if ((parseFloat(this.formData.international_tax_percentage) || 0) < 0) {
        this.addValidationError('El impuesto internacional no puede ser negativo', 'international_tax_percentage')
      }

      if ((parseFloat(this.formData.national_tax_percentage) || 0) < 0) {
        this.validationErrors.push('El impuesto nacional no puede ser negativo')
      }

      if (targetStatus === CLOSURE_STATUS.VALID) {
        // Zero is a valid amount: a closure can carry only a WHT adjustment, and the API accepts
        // it through the whole cycle. Only a missing or non-numeric value is rejected here.
        if (this.parseNumber(this.formData.amount) === null) {
          this.addValidationError('Debe ingresar un monto', 'amount')
        }

        if (!this.formData.agency_commission_mode) {
          this.addValidationError('Debe seleccionar un modo de comisión de agencia', 'agency_commission_mode')
        }
      }

      // Validate date overlap
      this.validateDateOverlap()

      return this.validationErrors.length === 0
    },

    /**
     * Register a validation error and the fields it belongs to, so they can be outlined in red
     */
    addValidationError(message, ...fieldNames) {
      if (!this.validationErrors.includes(message)) {
        this.validationErrors.push(message)
      }

      fieldNames.forEach((fieldName) => {
        if (fieldName && !this.erroredFields.includes(fieldName)) {
          this.erroredFields.push(fieldName)
        }
      })
    },

    async onSaveChoice(targetStatus) {
      this.isSaveChoiceModalOpen = false

      if (!targetStatus) {
        return
      }

      if (
        this.isEditMode &&
        this.closure &&
        this.closure.status === CLOSURE_STATUS.VALID &&
        targetStatus === CLOSURE_STATUS.DRAFT
      ) {
        this.$vs.notify({
          title: 'Acción no permitida',
          text: 'No es posible editar un cierre válido y guardarlo como borrador',
          color: 'warning'
        })
        return
      }

      await this.handleSubmit(targetStatus)
    },

    onSaveClick() {
      this.isSaveChoiceModalOpen = true
    },

    handleSaveChoiceClose() {
      this.isSaveChoiceModalOpen = false
    },

    /**
     * Handle form submission
     */
    async handleSubmit(targetStatus) {
      if (!this.validateForm(targetStatus)) {
        return
      }

      this.isLoading = true

      try {
        this.onBillingInformationChange(this.formData.billing_information_id)

        const submitData = formatClosureForSubmit({
          ...this.formData,
          status: targetStatus
        })

        let response

        if (this.isEditMode) {
          // Editing keeps history: cancel previous active closure and create a new one
          await this.$http.delete(
            `${appConfig.apiUrl}/campaigns/${this.campaignId}/sales-closures/${this.closure.id}`,
            { data: { cancellation_comment: 'Anulación automática por edición del cierre' } }
          )
        }

        // Create new closure
        response = await this.$http.post(
          `${appConfig.apiUrl}/campaigns/${this.campaignId}/sales-closures`,
          submitData
        )

        this.$vs.notify({
          title: 'Éxito',
          text: this.isEditMode
            ? 'Cierre anterior anulado y nuevo cierre guardado correctamente'
            : 'Cierre guardado correctamente',
          color: 'success'
        })

        this.$emit('save', response.data)
        this.isOpen = false
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Error al guardar el cierre',
          color: 'danger'
        })
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Handle modal close
     */
    handleClose() {
      this.$emit('cancel')
      this.isOpen = false
    },

    /**
     * Mark a field as edited by user. Removes suggestion/default border.
     */
    markFieldAsEdited(fieldName) {
      if (this.fieldStates[fieldName]) {
        this.$set(this.fieldStates[fieldName], 'edited', true)
      }

      // Editing a field clears its validation error, so the red outline disappears right away
      this.erroredFields = this.erroredFields.filter(field => field !== fieldName)
    },

    /**
     * Mark a field as having a system suggestion (data from backend with real values)
     */
    markFieldAsSuggested(fieldName) {
      if (this.fieldStates[fieldName]) {
        this.$set(this.fieldStates[fieldName], 'isSuggested', true)
        this.$set(this.fieldStates[fieldName], 'isDefault', false)
        this.$set(this.fieldStates[fieldName], 'edited', false)
      }
    },

    /**
     * Mark a field as default (no real data in system, using default value)
     */
    markFieldAsDefault(fieldName) {
      if (this.fieldStates[fieldName]) {
        this.$set(this.fieldStates[fieldName], 'isDefault', true)
        this.$set(this.fieldStates[fieldName], 'isSuggested', false)
        this.$set(this.fieldStates[fieldName], 'edited', false)
      }
    },

    /**
     * Get CSS classes for a field based on its state
     * Returns: 'field-error' (red) | 'field-suggested' (green) | 'field-default' (orange) | '' (no border)
     */
    getFieldBorderClass(fieldName) {
      // A failed validation outranks every other state
      if (this.erroredFields.includes(fieldName)) return 'field-error'

      if (!this.fieldStates[fieldName]) return ''
      const state = this.fieldStates[fieldName]

      // If user edited, no border
      if (state.edited) return ''

      // If suggested with real data, green
      if (state.isSuggested) return 'field-suggested'

      // If default value, orange
      if (state.isDefault) return 'field-default'

      return ''
    }
  }
}
</script>
